import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { appToast } from "../components/notification/app-toast";
import { useAppDispatch } from "../store";

type ThunkAction<TData, TVariables> = (variables: TVariables) => (
  dispatch: any
) => Promise<{
  success: boolean;
  data?: TData;
  message?: string;
}>;

interface CustomMutationOptions<TData, TVariables, TContext>
  extends UseMutationOptions<TData, Error, TVariables, TContext> {
  showErrorToast?: boolean;
}

export function useMutationWithDispatch<
  TData = any,
  TVariables = void,
  TContext = unknown
>(
  thunkAction: ThunkAction<TData, TVariables>,
  options?: CustomMutationOptions<TData, TVariables, TContext>
) {
  const dispatch = useAppDispatch();
  const { showErrorToast = false, ...restOptions } = options ?? {};

  return useMutation<TData, Error, TVariables, TContext>({
    ...restOptions,
    retry: false,
    mutationFn: async (variables: TVariables) => {
      const thunk = thunkAction(variables);
      const res = await dispatch(thunk);

      if (res && res.success === false) {
        throw new Error(res.message || "Something went wrong");
      }

      return res as TData;
    },
    onError: (error, variables, context) => {
      if (showErrorToast) {
        appToast.error(error?.message || "An unexpected error occurred");
      }
      restOptions.onError?.(error, variables, context);
    },
  });
}
